import { HeaderContext } from '@tanstack/react-table';
import { ContactType } from 'constants/contacts';
import { capitalize } from 'lodash';
import { CompanyContact, Contact } from 'types/domain';

type ContactTableKey =
    | 'total_contributions'
    | 'notifications'
    | 'total_soft_credits'
    | 'additional_emails'
    | 'additional_phones'
    | 'additional_addresses'
    | keyof Contact;

const curriedTransformTableKeyToReadable = (header: HeaderContext<Contact, unknown>) =>
    transformTableKeyToReadable(header.column.id as ContactTableKey);

const transformTableKeyToReadable = (key: ContactTableKey) => {
    switch (key) {
        case 'notifications':
            return 'Communication preferences';
        case 'total_contributions':
            return 'Contributions';
        case 'total_soft_credits':
            return 'Soft credits';
        case 'additional_emails':
        case 'additional_phones':
        case 'additional_addresses':
            return capitalize(key).split('_').join(' ');
        default:
            return contactKeyToReadable(key);
    }
};

const contactKeyToReadable = (key: keyof Contact | keyof CompanyContact) => {
    switch (key) {
        case 'address_1':
            return 'Street 1';
        case 'address_2':
            return 'Street 2';
        case 'dob':
            return 'Birthday';
        case 'primary_email':
            return 'Email';
        case 'primary_phone':
            return 'Phone';
        case 'primary_address':
        case 'primary_address_formatted':
            return 'Full address';
        case 'created_at':
            return 'Contact since';
        case 'stats':
            return 'Contributions';
        case 'point_of_contact':
            return 'Point of contact';
        case 'company_name':
        case 'associated_companies':
            return 'Company';
        case 'first_name':
        case 'last_name':
        case 'middle_name':
        case 'facebook_url':
        case 'linkedin_url':
        case 'website_url':
        case 'twitter_url':
        case 'x_url':
        case 'custom_fields':
        case 'employer':
            return capitalize(key.split('_').join(' '));
        case 'phone':
        case 'gender':
        case 'note':
        case 'title':
        case 'tags':
        case 'state':
        case 'city':
        case 'zip':
        case 'country':
        case 'type':
        case 'company':
            return capitalize(key);
        case 'id':
        case 'years_transacted':
        case 'donorsearch':
        case 'donorsearch_last_updated_at':
        case 'prefix':
        case 'suffix':
        case 'is_email_subscribed':
        case 'is_phone_subscribed':
        case 'is_address_subscribed':
        case 'archived_at':
        case 'household_id':
        case 'phones':
        case 'reports':
        case 'emails':
        case 'addresses':
        case 'updated_at':
        default:
            return key;
    }
};

type ComputedContactProps = {
    isCompany: boolean;
    contactName: string;
    linkUrl: string;
};

const getComputedContactProps = (contact: Contact): ComputedContactProps => {
    const { type, first_name, last_name, company_name, id } = contact;
    const isCompany = type === ContactType.Company;
    const contactName = isCompany ? company_name : `${first_name} ${last_name}`;
    const linkUrl = isCompany ? `companies/${id}` : `contacts/${id}`;

    return {
        isCompany,
        contactName,
        linkUrl,
    };
};

export {
    transformTableKeyToReadable,
    contactKeyToReadable,
    curriedTransformTableKeyToReadable,
    getComputedContactProps,
};
export type { ContactTableKey, ComputedContactProps };
